import styled from '@emotion/styled'
import { faCopy } from '@fortawesome/free-regular-svg-icons'
import { faEuroSign } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import { Badge, Modal } from 'react-bootstrap'
import Breakpoint from '../../../Breakpoint'
import files from '../../../assets/templates-files/files'
import CatchError from '../../../components/global/CatchError'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import TooltipBva from '../../../components/global/TooltipBva'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import Table from '../../../components/table/Table'
import { StyledButtonLink } from '../../../components/table/Td'
import { SERVER_URL, apiHeader } from '../../../config'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { UserType, roles } from '../../../types/user.type'
import { StyledButton } from '../Login'

const helpTextModel = (
    <>
        Il est recommandé d'utiliser{' '}
        <a href="https://docs.google.com" target="_blank">
            Google Sheets
        </a>{' '}
        pour remplir les données.
    </>
)

const AdminUser = () => {
    const user = useContext(UserContext)
    const [admins, setAdmins] = useState<UserType[] | undefined>()
    const [members, setMembers] = useState<UserType[] | undefined>()
    const [membersToDisplay, setMembersToDisplay] = useState<UserType[] | undefined>()
    const [importMemberModal, setImportMemberModal] = useState<boolean>(false)
    const [tournamentMoneyModal, setTournamentMoneyModal] = useState<boolean>(false)
    const [importFile, setImportFile] = useState<File | undefined>()
    const [isLoadingImport, setIsLoadingImport] = useState(false)

    const lng = useLangSelected()
    const [currentlang, setCurrentLang] = useState(lng)

    const tableHeaderAdmin = ['#', "Nom d'utilisateur", 'Email', '', '']
    const tableHeaderMember = ['#', 'Prénom', 'Nom', "Nom d'utilisateur", 'Email', 'Compte tournoi', '', '']

    useEffect(() => {
        if (!admins || currentlang !== lng) {
            setCurrentLang(lng)
            axios
                .get(`${SERVER_URL}/users`, apiHeader(user.tokenAdmin))
                .then(({ data }) => {
                    setAdmins(
                        data
                            .filter((user: any) => user.role === roles.ROLE_ADMIN)
                            .map((user: UserType) => ({
                                id: user.id,
                                pseudo: user.pseudo,
                                email: user.email,
                                edit: `/${lng}/bva-admin/utilisateurs/${user.id}`,
                                remove: true,
                            }))
                    )

                    setMembers(
                        data
                            .filter((user: any) => user.role === roles.ROLE_MEMBER)
                            .map((user: UserType) => ({
                                id: user.id,
                                firstname: user.firstName,
                                lastname: user.lastName,
                                pseudo: user.pseudo,
                                email: user.email,
                                gender: user.gender,
                                dayOfBirth: moment(user.dayOfBirth).format('DD/MM/yyyy'),
                                tournamentMoney: user.tournamentMoney + '€',
                                edit: `/${lng}/bva-admin/utilisateurs/${user.id}`,
                                remove: true,
                            }))
                    )

                    setMembersToDisplay(
                        data
                            .filter((user: any) => user.role === roles.ROLE_MEMBER)
                            .map((user: UserType) => ({
                                id: user.id,
                                firstname: user.firstName,
                                lastname: user.lastName,
                                pseudo: user.pseudo,
                                email: user.email,
                                tournamentMoney: user.tournamentMoney + '€',
                                edit: `/${lng}/bva-admin/utilisateurs/${user.id}`,
                                remove: true,
                            }))
                    )
                })
                .catch((err) =>
                    CatchError(err.response.data, () => {
                        localStorage.removeItem('tokenAdmin')
                        user.adminData = undefined
                        window.location.reload()
                    })
                )
        }
    }, [user, admins])

    if (!admins || !members || !membersToDisplay) return <Loader />

    const editTournamentMoney = () => {
        if (importFile) {
            const reader = new FileReader()
            reader.readAsText(importFile)
            reader.onload = function (e: any) {
                const csvSplitByLine: string[] = e.target.result.split('\r\n')
                const separator = csvSplitByLine[0].includes(';') ? ';' : ','
                let csvLineSplitByCells: string[][] = csvSplitByLine.map((line: string) =>
                    line.replaceAll('"', '').split(separator)
                )

                csvLineSplitByCells = csvLineSplitByCells.filter(
                    (cell, index) => index !== 0 && cell.length === csvLineSplitByCells[0].length
                )

                axios
                    .post(
                        `${SERVER_URL}/users/import/tournament-money-by-csv-data`,
                        csvLineSplitByCells,
                        apiHeader(user.tokenAdmin)
                    )
                    .then((res) => {
                        sweetAlertSuccess(`${res.data} comptes tournois ont été modifié.`).then(() => {
                            window.location.reload()
                        })
                        setImportMemberModal(false)
                    })
                    .catch((err) => {
                        CatchError(err.response.data)
                    })
            }
        }
    }

    const importCsvData = () => {
        if (importFile) {
            const reader = new FileReader()
            reader.readAsText(importFile)
            reader.onload = function (e: any) {
                const csvSplitByLine: string[] = e.target.result.split('\r\n')
                let csvLineSplitByCells: string[][] = csvSplitByLine.map((line: string) =>
                    line.replaceAll('"', '').split(',')
                )

                csvLineSplitByCells = csvLineSplitByCells.filter(
                    (cell, index) => index !== 0 && cell.length === csvLineSplitByCells[0].length
                )

                axios
                    .post(`${SERVER_URL}/users/import/members`, csvLineSplitByCells, apiHeader(user.tokenAdmin))
                    .then(() => {
                        sweetAlertSuccess(
                            `Les membres manquant vont être ajouté, ça peut prendre un certains temps selon le volume des adhérents inscrit.`
                        ).then(() => {
                            window.location.reload()
                        })
                        setImportMemberModal(false)
                    })
                    .catch((err) => {
                        CatchError(err.response.data)
                    })
            }
        }
    }

    const deleteById = (id: number) => {
        axios
            .delete(`${SERVER_URL}/users/${id}`, apiHeader(user.tokenAdmin))
            .then((res) => {
                sweetAlertSuccess('La suppression de cette utilisateur est réussi').then(() => {
                    setAdmins([...admins.filter((user: { id: number }) => user.id !== res.data.id)])
                })
            })
            .catch((err) =>
                CatchError(err.response.data, () => {
                    localStorage.removeItem('tokenAdmin')
                    user.adminData = undefined
                    window.location.reload()
                })
            )
    }

    return (
        <>
            <TitleWrapper>
                <Title>Utilisateurs</Title>
                <HeaderBtnWrapper>
                    <Btn onClick={() => setImportMemberModal(true)}>Importer des adhérents</Btn>
                    <LinkBtn to={`/${lng}/bva-admin/utilisateurs/creer`} className="btn btn-primary">
                        Créer un utilisateur
                    </LinkBtn>
                </HeaderBtnWrapper>
            </TitleWrapper>
            {importMemberModal && (
                <Modal centered show={true} onHide={() => setImportMemberModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Ajouter des membres</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <TooltipBva tootipText={helpTextModel} placement="right">
                            <a href={files.model_adherents} className="btn btn-primary" download>
                                Télécharger le model
                            </a>
                        </TooltipBva>

                        <ImportInput
                            type="file"
                            accept=".csv"
                            onChange={(e) => e.target?.files?.[0] && setImportFile(e.target.files[0])}
                        />
                        <StyledButton variant="primary" type="button" onClick={() => importCsvData()}>
                            Importer
                        </StyledButton>
                    </Modal.Body>
                </Modal>
            )}
            <AdminContainer>
                <TableTitle>
                    Liste des administrateurs <BvaBadge>{admins.length}</BvaBadge>
                </TableTitle>
                <UserTableWrapper>
                    <Table tableData={admins} tableHeader={tableHeaderAdmin} deleteById={deleteById} />
                </UserTableWrapper>

                <TitleWrapper>
                    <TableTitle>
                        Liste des membres <BvaBadge>{members.length}</BvaBadge>
                    </TableTitle>{' '}
                    <BtnWrapper>
                        <Btn
                            variant="primary"
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(members.map((mem) => mem.email).join(';'))
                                sweetAlertSuccess('Les adresses mail sont copiées.')
                            }}
                        >
                            <IconBtn icon={faCopy} /> Toutes les adresses mails
                        </Btn>
                        <Btn
                            variant="primary"
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(
                                    members
                                        .filter((mem) => mem.gender === 'F')
                                        .map((mem) => mem.email)
                                        .join(';')
                                )
                                sweetAlertSuccess('Les adresses mail sont copiées.')
                            }}
                        >
                            <IconBtn icon={faCopy} /> Adresses mails des femmes
                        </Btn>
                        <Btn
                            variant="primary"
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(
                                    members
                                        .filter((mem) => mem.gender === 'H')
                                        .map((mem) => mem.email)
                                        .join(';')
                                )
                                sweetAlertSuccess('Les adresses mail sont copiées.')
                            }}
                        >
                            <IconBtn icon={faCopy} /> Adresses mails des hommes
                        </Btn>
                        <Btn
                            variant="primary"
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(
                                    members
                                        .filter((mem) => +mem.dayOfBirth.split('/')[2] > 2006)
                                        .map((mem) => mem.email)
                                        .join(';')
                                )
                                sweetAlertSuccess('Les adresses mail sont copiées.')
                            }}
                        >
                            <IconBtn icon={faCopy} /> Adresses mails des jeunes (-16 ans)
                        </Btn>
                        <Btn variant="primary" type="button" onClick={() => setTournamentMoneyModal(true)}>
                            <IconBtn icon={faEuroSign} /> Modifier les comptes tournois
                        </Btn>
                    </BtnWrapper>
                </TitleWrapper>
                <Table tableData={membersToDisplay} tableHeader={tableHeaderMember} deleteById={deleteById} />
                {tournamentMoneyModal && (
                    <Modal
                        centered
                        show={true}
                        onHide={() => {
                            setTournamentMoneyModal(false)
                        }}
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Modifier le compte tournoi</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <TooltipBva tootipText={helpTextModel} placement="right">
                                <a href={files.model_compte_tournoi} className="btn btn-primary" download>
                                    Télécharger le model
                                </a>
                            </TooltipBva>

                            <ImportInput
                                type="file"
                                accept=".csv"
                                onChange={(e) => e.target?.files?.[0] && setImportFile(e.target.files[0])}
                            />

                            <StyledButton
                                variant="primary"
                                type="button"
                                onClick={() => {
                                    editTournamentMoney()
                                    setTournamentMoneyModal(false)
                                    setImportFile(undefined)
                                }}
                            >
                                Modifier
                            </StyledButton>
                        </Modal.Body>
                    </Modal>
                )}
            </AdminContainer>
        </>
    )
}

const HeaderBtnWrapper = styled.div`
    display: flex;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        flex-direction: column;
    }
`

const IconBtn = styled(FontAwesomeIcon)`
    font-size: 20px;
`

const BtnWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    margin-left: 8px;
    width: 75%;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        flex-direction: column;
        width: 100%;
        flex-wrap: nowrap;
    }
`

const BvaBadge = styled(Badge)`
    display: flex;
    align-items: center;
    width: min-content;
    height: 28px;
    background: var(--primary-color) !important;
    margin-left: 8px;
`

const SelectWrapper = styled.div`
    margin-bottom: 16px;
`

const UserTableWrapper = styled.div`
    margin-bottom: 32px;
`

const TableTitle = styled(Title)`
    display: flex;
    width: 25%;
    align-items: center;
    margin-bottom: 8px;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        width: 100%;
    }
`

const LinkBtn = styled(StyledButtonLink)`
    margin-right: 8px;
    margin-bottom: 8px;

    @media (max-width: ${Breakpoint.TABLET_L}) {
        margin-right: 0;
    }
`

const Btn = styled(StyledButton)`
    margin-right: 8px;
    margin-bottom: 8px;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin-right: 0;
        width: 100%;
    }
`

const ImportInput = styled.input`
    display: block;
    width: 100%;
    webkit-appearance: none;
    margin-right: 8px;
    margin-top: 16px;
    cursor: pointer;
    margin-bottom: 16px;
`

export const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;

    & .btn {
        height: 38px;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        flex-direction: column;
    }
`

export const AdminContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: calc(100vh - 278px);
    padding: 24px;
    border-radius: 6px;
    background-color: white;
`

export default AdminUser
